import React, { PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Button } from '@ff-os/shared-react/components/buttons/button/Button';
import { UploadError } from '@ff-os/shared-react/components/file-uploader/FileUploader';
import { UploadMethodsProps } from '@ff-os/shared-react/components/upload-methods/CreateUploadMethod';
import { UploadMethodComponent } from '@ff-os/shared-react/components/upload-methods/partials/UploadMethodComponent';
import { UploadMethods } from '@ff-os/shared-react/components/upload-methods/UploadMethods';
import { WindowBody } from '@ff-os/shared-react/components/window/partials/WindowBody';
import { WindowFooter } from '@ff-os/shared-react/components/window/partials/WindowFooter';
import { WindowHeader } from '@ff-os/shared-react/components/window/partials/WindowHeader';
import { Window } from '@ff-os/shared-react/components/window/Window';
import { WindowStackItem } from '@ff-os/shared-react/context/window-stack/WindowStackItem';
import { useMobile } from '@ff-os/shared-react/hooks/useMobile';
import { PhotoPrintsPdpDispatch } from '@ff-scene-plugins/photo-prints-redux/store';
import { usePhotoPrintsUploadMethods } from '../../../createPhotoPrintsUploadMethods';
import { createGroup } from '../../../thunks/createGroup';
import { incrementWithScheduledReset } from '../../../thunks/withScheduledReset';
import { ChangeVariantWindowStep } from '../ChangeVariantWindowStep';
import styles from '../ChangeVariantWindow.module.scss';

const _UploadMethodsStep = ({
    paperId, sizeId, borderId,
    groupId, createGroup, close,
    save, onError,
    stepper, onUpload
}: Props) => {

    const { t } = useTranslation();
    const isMobile = useMobile();

    const methods = usePhotoPrintsUploadMethods({
        dropzone: { onChange: save, onError },
        externalUpload: {
            groupId: groupId,
            closeOnUpload: true,
            onOpen: () => createGroup(paperId!, sizeId!, groupId, borderId),
            onChange: save
        }
    });

    const closeWindow = () => setActiveSource('Dropzone');

    const [activeSource, setActiveSource] = useState<keyof UploadMethodsProps>('Dropzone');

    return (
        <ChangeVariantWindowStep className={styles.container} stepper={stepper}>
            <div className={styles.uploadMethodsContainer}>
                <UploadMethods
                    onUpload={onUpload}
                    close={close}
                    methods={methods}
                    setActiveSource={setActiveSource} />
            </div>

            {activeSource !== 'Dropzone' && (
                <WindowStackItem>
                    <Window close={closeWindow} fullHeight={isMobile}>
                        <WindowHeader onClose={closeWindow} />
                        <WindowBody>
                            <UploadMethodComponent
                                onUpload={onUpload}
                                close={closeWindow}
                                method={methods[activeSource]!} />
                        </WindowBody>
                        <WindowFooter>
                            <Button style='outline' onClick={closeWindow}>{t('commons.close')}</Button>
                        </WindowFooter>
                    </Window>
                </WindowStackItem>
            )}
        </ChangeVariantWindowStep>
    );
};

type OwnProps = {

    stepper?: () => JSX.Element;

    groupId: string;
    paperId?: string;
    sizeId?: string;
    borderId?: string;
};

type Props = PropsWithChildren<OwnProps & {
    createGroup: (paperId: string, sizeId: string, groupId: string, borderId: string | undefined) => void;
    save: (files?: File[]) => void;
    close: () => void;
    onUpload?: () => void;

    onError: (errors: UploadError[]) => void;
}>;

const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    createGroup: (paperId: string, sizeId: string, groupId: string, borderId: string | undefined) => {
        dispatch(createGroup(paperId, sizeId, groupId, borderId));
    },
    onError: (errors: UploadError[]) => {
        dispatch(incrementWithScheduledReset(ownProps.groupId, 'failedUploadsCount', errors.length));
    }
});

export const UploadMethodsStep = connect(
    null,
    mapDispatchToProps
)(_UploadMethodsStep);